import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    INVOICE_STATUS_OPTIONS,
    INVOICE_TYPES,
} from '@/modules/Invoice/constants'
import { getInvoices } from '@/modules/Invoice/services'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    getInvoiceListColumns,
    getInvoiceListFilterItems,
} from '../utils/invoice-list-config'
import { useEInvoiceAccess } from './use-e-invoice-access'

/**
 * Shared filters interface for Invoice tabs
 * Includes invoice_status to preserve filters when switching tabs
 */
interface InvoiceFilters {
    invoice_status: string[]
    contract_role: string[]
    contract_direction: string[]
    product_group_name: string[]
    account_manager_codes: string[]
    invoice_apply_startdate: string[]
    invoice_apply_startdate_to: string[]
    invoice_apply_enddate: string[]
    payment_request_status: string[]
    reconciliation_statement_status: string[]
    e_invoice_status: string[] // 'none' = chưa phát hành hoá đơn điện tử
}

/**
 * Business logic hook for Invoice Non-Invoicable List
 * Filters invoices that are non-invoicable and revenue confirmed
 */
export const useInvoiceNonInvoicable = () => {
    const { t } = useTranslation(['invoice'])
    // Cột / filter "HĐ điện tử": chỉ nhóm Kế toán / BOM / BOD / Admin
    const { canView: canViewEInvoice } = useEInvoiceAccess()

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<InvoiceFilters>({
        defaults: {
            sort_by: 'invoice_id',
            sort_order: 'desc',
            page_size: 20,
        },
        filterDefaults: {
            invoice_status: [],
            contract_role: [],
            contract_direction: [],
            product_group_name: [],
            account_manager_codes: [],
            invoice_apply_startdate: [],
            invoice_apply_startdate_to: [],
            invoice_apply_enddate: [],
            e_invoice_status: [],
            payment_request_status: [],
            reconciliation_statement_status: [],
        },
    })

    // Form for filter controls
    const { control } = useForm<InvoiceFilters>({
        defaultValues: filters,
    })

    // Build query params merging URL filters with tab-specific filters
    const apiParams = useMemo(() => {
        // Parse URL filters
        let urlFilters: Record<string, unknown> = {}
        if (queryParams.filters) {
            try {
                urlFilters = JSON.parse(atob(queryParams.filters))
            } catch {
                urlFilters = {}
            }
        }

        // Default invoice statuses for this tab
        const defaultStatuses = [
            INVOICE_STATUS_OPTIONS.Approved,
            INVOICE_STATUS_OPTIONS.Paid,
            INVOICE_STATUS_OPTIONS.Done,
        ]

        // Merge with tab-specific filters (non-invoicable, revenue confirmed, etc.)
        // Use user-selected invoice_status if provided, otherwise use defaults
        const mergedFilters = {
            ...urlFilters,
            revenue_confirmed: [true],
            invoicable: [false],
            invoice_type: [INVOICE_TYPES.Default],
            invoice_status:
                Array.isArray(urlFilters.invoice_status) &&
                urlFilters.invoice_status.length > 0
                    ? urlFilters.invoice_status
                    : defaultStatuses,
        }

        return {
            ...queryParams,
            filters: btoa(JSON.stringify(mergedFilters)),
        }
    }, [queryParams])

    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICES, 'non-invoicable', apiParams],
        queryFn: () => getInvoices(apiParams),
        placeholderData: true,
    })

    // Filter out invoice_exporter_code column (only shown in Invoicable tab)
    const columns = useMemo(
        () =>
            getInvoiceListColumns(t, { showEInvoice: canViewEInvoice }).filter(
                (col) => col.key !== 'invoice_exporter_code'
            ),
        [t, canViewEInvoice]
    )
    const filterItems = useMemo(
        () =>
            getInvoiceListFilterItems(t, setFilter, {
                showEInvoice: canViewEInvoice,
            }),
        [t, setFilter, canViewEInvoice]
    )

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        invoices: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,
        params,
        columns,
        // Filter support
        filters,
        control,
        setFilter,
        filterItems,
        searchInputProps,
        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    }
}
